import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Building2, FileCheck, Stamp } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import translations from '@/data/translations';
import { MEDIA } from '@/data/plotData';
import { LANDING } from '@/constants/testIds';

const badges = [
  { icon: Building2, key: 'bankLoan' },
  { icon: FileCheck, key: 'clearTitle' },
  { icon: Stamp, key: 'immediate' },
];

export default function TrustZone() {
  const { lang } = useLanguage();
  const t = translations[lang].trust;

  return (
    <section data-testid={LANDING.trustZone} className="relative bg-[#2C4C3B] overflow-hidden">
      {/* Subtle pattern */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
      }} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
          {/* RERA block: QR + details */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="flex items-center gap-5"
          >
            {/* QR Code — large enough to scan */}
            <div
              data-testid="rera-qr-image"
              className="w-24 h-24 sm:w-28 sm:h-28 bg-white rounded-xl p-1.5 flex-shrink-0 shadow-lg shadow-black/10"
            >
              <img
                src={MEDIA.reraQR}
                alt="RERA QR Code - Scan to verify"
                className="w-full h-full object-contain"
                style={{ imageRendering: 'pixelated' }}
              />
            </div>

            <div>
              <div className="flex items-center gap-1.5 mb-1">
                <ShieldCheck className="w-4 h-4 text-[#D48C57]" />
                <p className="text-[#D48C57] text-[11px] font-semibold uppercase tracking-[0.15em]">
                  {t.reraLabel}
                </p>
              </div>
              <p data-testid={LANDING.reraNumber} className="text-white font-mono text-base sm:text-lg font-bold tracking-wide">
                {t.rera}
              </p>
              <p className="text-white/40 text-[10px] mt-1.5 max-w-[200px] leading-relaxed">
                {lang === 'en' ? 'Scan to verify on TN RERA portal' : 'TN RERA தளத்தில் சரிபார்க்க ஸ்கேன் செய்யவும்'}
              </p>
            </div>
          </motion.div>

          {/* Divider */}
          <div className="hidden lg:block w-px h-16 bg-white/15" />

          {/* Trust badges */}
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
            {badges.map(({ icon: Icon, key }, i) => (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 + i * 0.1, duration: 0.4 }}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/8 border border-white/10"
              >
                <Icon className="w-4 h-4 text-[#D48C57]" />
                <span className="text-white/90 text-sm font-medium whitespace-nowrap">{t[key]}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
